import { GetALLUsers, type UserData } from '@/apis/onboarding';
import { useToken } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';
import { useUnAuthorize } from '@/hooks';
import { message } from 'antd';
import { useCallback, useState } from 'react';

export interface UseFetchUsersResult {
  userList: UserData[];
  totalPages: number;
  maxPageSize: number;
  fetchUsers: (
    getDisabled?: boolean,
    page?: number,
    search?: string
  ) => Promise<UserData[]>;
}

const useFetchUsers = (): UseFetchUsersResult => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnAuthorize();

  const [userList, setUserList] = useState<UserData[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [maxPageSize, setMaxPageSize] = useState(0);

  const fetchUsers = useCallback(
    async (getDisabled = true, page = 1, search = ''): Promise<UserData[]> => {
      try {
        showSpin();

        const res = await GetALLUsers(token, getDisabled, page, search);
        const data = res?.data;

        if (data?.data?.is_success) {
          setUserList(data.data.data ?? []);
          setTotalPages(data.data.page_count ?? 0);
          setMaxPageSize(data.data.total_count ?? 0);

          return data.data.data ?? [];
        }

        message.error(data?.data?.message);
        return [];
      } catch (error) {
        unauthorize(error);
        return [];
      } finally {
        hideSpin();
      }
    },
    [token, showSpin, hideSpin, unauthorize]
  );

  return {
    userList,
    totalPages,
    maxPageSize,
    fetchUsers,
  };
};

export default useFetchUsers;
